import { CalculatorFormData, CalculatorResults } from './types';
import { renderToString } from 'react-dom/server';
import React from 'react';
import { getRecommendedFeatures, calculateFeatureTotalImpact } from '../../data/roosterFeatures';

// We'll use this to format currencies
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};

// Format numbers with thousands separators
const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(value);
};

// Helper function to ensure numeric value
const getNumericValue = (value: number | string, defaultValue = 0): number => {
  if (typeof value === 'string') {
    return value === '' ? defaultValue : Number(value);
  }
  return value;
};

// Helper function to provide component descriptions from Screen 4 tooltips
const getComponentDescription = (componentName: string): string => {
  switch (componentName) {
    case 'Scheduling Velocity (SV)':
      return 'Measures how quickly interviews are scheduled and confirmed. This includes time from interview request to confirmation and reduction in scheduling-related delays.';
    case 'Scheduling Efficiency (SE)':
      return 'Measures the administrative effort and time required to coordinate interviews, including calendar management, communications, and logistics handling.';
    case 'Candidate Experience (CE)':
      return 'Evaluates how candidate-friendly your scheduling process is, including flexibility, convenience, and clarity of communications.';
    case 'Scheduling Integrity (SI)':
      return 'Assesses the consistency and dependability of your scheduling process, including no-show rates, cancellations, and rescheduling frequency.';
    case 'Scheduling Utilization (SU)':
      return 'Evaluates how effectively your process utilizes interviewer time, balances interviewer workloads, and maximizes resource availability.';
    default:
      return 'This component measures a critical aspect of your scheduling process efficiency.';
  }
};

// Helper function to provide improvement descriptions for each TAS component
const getComponentImprovementText = (componentName: string): string => {
  switch (componentName) {
    case 'Scheduling Velocity (SV)':
      return 'Self-scheduling capabilities and smart calendar integrations significantly reduce the time to confirm interviews.';
    case 'Scheduling Efficiency (SE)':
      return 'Automated scheduling workflows reduce administrative time spent coordinating interviews by 60-80%.';
    case 'Candidate Experience (CE)':
      return 'Candidate-controlled scheduling with personalized communications improves satisfaction and engagement.';
    case 'Scheduling Integrity (SI)':
      return 'Automated reminders and better scheduling options reduce rescheduling and no-show rates.';
    case 'Scheduling Utilization (SU)':
      return 'Smart interviewer selection and availability management ensure optimal resource allocation.';
    default:
      return 'Rooster Scheduling provides comprehensive improvements across all aspects of the interview scheduling process.';
  }
};

// Component to render ROI Analysis for PDF capture
// This version of the component is for code-generated HTML
// We'll create transform section separately in the print window
const ROIAnalysisReport: React.FC<{
  formData: CalculatorFormData;
  results: CalculatorResults;
  componentData: any[];
  improvementData: any[];
  includeTransformSection?: boolean;
  scenarioMode?: 'conservative' | 'moderate' | 'optimistic';
  annualSubscription?: number;
  implementationInvestment?: number;
  paybackPeriod?: number;
  year1ROI?: number;
  steadyStateROI?: number;
  enhancedSavings?: any;
  topPerformerTAS?: number;
  industryAverageTAS?: number;
}> = ({ formData, results, componentData, improvementData, includeTransformSection = true, scenarioMode = 'moderate', annualSubscription = 0, implementationInvestment = 0, paybackPeriod = 0, year1ROI = 0, steadyStateROI = 0, enhancedSavings, topPerformerTAS = 75, industryAverageTAS = 50 }) => {
  return (
    <div className="pdf-content" style={{ fontFamily: 'Arial, sans-serif' }}>

      {/* Company Information */}
      <div className="organization-info" style={{
        border: '1px solid #E5E7EB',
        borderRadius: '4px',
        padding: '15px',
        marginBottom: '20px',
        backgroundColor: '#F9FAFB'
      }}>
        <h2 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0284c7' }}>Organization Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Industry:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formData.industry}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>{formData.inputMode === 'fte' ? 'Total Employees:' : 'Annual Hires:'}</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatNumber(getNumericValue(formData.inputMode === 'fte' && formData.fteCount ? formData.fteCount : formData.annualHires))}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Average Salary:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(getNumericValue(formData.avgSalary))}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Daily Vacancy Cost:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(getNumericValue(formData.dailyVacancyCost))}</div>
          </div>
        </div>
      </div>
      
      {/* TAS Score Analysis */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          TAS Score Analysis
        </h2>

        <div className="tas-circle-container" style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '4px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          {/* Benchmarks Group */}
          <div style={{ display: 'flex', justifyContent: 'space-around', flex: '1' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Industry Average</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#9CA3AF',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {industryAverageTAS}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Your Current Score</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#8B5CF6',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {results.currentTAS}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Top Performers</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#10B981',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {topPerformerTAS}
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div style={{
            width: '1px',
            height: '80px',
            backgroundColor: '#D1D5DB',
            margin: '0 30px'
          }}></div>

          {/* Projected Score */}
          <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px', fontWeight: '600' }}>
              Your Projected Score
              <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px', fontWeight: 'normal' }}>with Rooster</div>
            </div>
            <div style={{
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0284c7',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '24px',
              borderRadius: '50%',
              margin: '0 auto',
              boxShadow: '0 4px 6px rgba(2, 132, 199, 0.3)'
            }}>
              {results.projectedTAS}
            </div>
            <div style={{ fontSize: '12px', color: '#059669', marginTop: '5px', fontWeight: '600' }}>
              +{results.projectedTAS - results.currentTAS} points
            </div>
          </div>
        </div>

        {/* Score explanation section */}
        <div style={{ marginBottom: '20px', backgroundColor: 'rgba(2, 132, 199, 0.1)', borderRadius: '4px', padding: '15px' }}>
          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            The <strong>Talent Acquisition Scheduling</strong> score measures the efficiency and effectiveness of your interview scheduling process on a scale from 0-100.
          </p>

          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            The average TAS score across {formData.industry} companies is around {industryAverageTAS}. Top performing organizations achieve scores of {topPerformerTAS} or higher.
            With your current score of {results.currentTAS}, your scheduling process is {results.currentTAS < industryAverageTAS ? 'below' : results.currentTAS > (industryAverageTAS + 10) ? 'above' : 'near'} the industry average.
          </p>

          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            Based on your selected improvement level, implementing Rooster Scheduling would increase your score to <strong>{results.projectedTAS}</strong> (a {formatNumber(getNumericValue(formData.tasImprovement))} point improvement), placing you in the <strong>{results.projectedTAS >= 75 ? 'top performing' : results.projectedTAS >= 70 ? 'high performing' : 'above average'}</strong> category.
          </p>

          <p style={{ fontSize: '14px', margin: '0', fontStyle: 'italic' }}>
            Rooster clients typically see a 75% or greater improvement in their TAS score within the first 90 days of implementation.
          </p>
        </div>

        <p style={{ marginBottom: '15px', fontSize: '14px' }}>
          Your TAS score is composed of five key components, each weighted based on its impact on the overall scheduling process. Here's how you score in each area:
        </p>

        <div style={{ marginBottom: '20px' }}>
          {componentData.map((component, index) => {
            const barWidth = `${component.value}%`;

            return (
              <div key={index} style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{`${component.name} (${component.weight})`}</div>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: component.color }}>{`${Math.round(component.value)}%`}</div>
                </div>
                <div style={{ width: '100%', backgroundColor: '#E5E7EB', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: barWidth,
                      height: '100%',
                      backgroundColor: component.color,
                      borderRadius: '5px 0 0 5px'
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#0284c7' }}>Component Descriptions</h3>

        <div style={{ marginBottom: '20px' }}>
          {componentData.map((component, index) => (
            <div key={index} style={{
              marginBottom: '15px',
              padding: '10px',
              border: `1px solid ${component.color}20`,
              borderLeft: `4px solid ${component.color}`,
              borderRadius: '4px',
              backgroundColor: '#FAFAFA'
            }} className="component-description">
              <div style={{ fontWeight: 'bold', fontSize: '14px', color: component.color, marginBottom: '5px' }}>{component.name}</div>
              <div style={{ fontSize: '14px' }}>{getComponentDescription(component.name)}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '5px' }}>
                <strong>How Rooster improves this:</strong> {getComponentImprovementText(component.name)}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="roi-summary" style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          ROI Analysis Summary
        </h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* TAS Score Card */}
          <div style={{ 
            border: '1px solid #E5E7EB', 
            borderRadius: '4px', 
            padding: '15px',
            backgroundColor: '#F9FAFB'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#0284c7', textAlign: 'center' }}>TAS Score</h3>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ width: '50%', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>Current:</div>
              <div style={{ width: '50%', fontSize: '18px', fontWeight: 'bold' }}>{results.currentTAS}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '50%', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>Projected:</div>
              <div style={{ width: '50%', fontSize: '18px', fontWeight: 'bold', color: '#10B981' }}>{results.projectedTAS}</div>
            </div>
          </div>
          
          {/* ROI Card */}
          <div style={{ 
            border: '1px solid #E5E7EB', 
            borderRadius: '4px', 
            padding: '15px',
            backgroundColor: '#F9FAFB'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#0284c7', textAlign: 'center' }}>Return on Investment</h3>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ width: '50%', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>Annual ROI:</div>
              <div style={{ width: '50%', fontSize: '18px', fontWeight: 'bold', color: '#10B981' }}>{(steadyStateROI || results.annualRoi || 0).toFixed(1)}x</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '50%', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>Payback Period:</div>
              <div style={{ width: '50%', fontSize: '18px', fontWeight: 'bold', color: '#10B981' }}>{paybackPeriod > 0 ? `${paybackPeriod} ${paybackPeriod === 1 ? 'month' : 'months'}` : '< 3 months'}</div>
            </div>
          </div>
        </div>
        
        {/* Annual Savings Card */}
        <div style={{ 
          border: '1px solid #E5E7EB', 
          borderRadius: '4px', 
          padding: '15px',
          backgroundColor: '#F9FAFB',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#0284c7', textAlign: 'center' }}>Steady State Annual Savings</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10B981', textAlign: 'center' }}>
            {formatCurrency(enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0)}
          </div>
        </div>
        
        {/* Year 1 vs Steady State ROI Comparison - NEW */}
        {(year1ROI > 0 || steadyStateROI > 0) && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Year 1 ROI Card */}
            <div style={{ 
              border: '1px solid #E5E7EB', 
              borderRadius: '4px', 
              padding: '15px',
              backgroundColor: '#FFF7ED'
            }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#EA580C', textAlign: 'center' }}>Year 1 ROI</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EA580C', textAlign: 'center' }}>
                {year1ROI.toFixed(1)}x
              </div>
              <div style={{ fontSize: '11px', color: '#9A3412', textAlign: 'center', marginTop: '5px' }}>
                Accounts for implementation timeline
              </div>
            </div>
            
            {/* Steady State ROI Card */}
            <div style={{ 
              border: '1px solid #E5E7EB', 
              borderRadius: '4px', 
              padding: '15px',
              backgroundColor: '#F0FDF4'
            }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#16A34A', textAlign: 'center' }}>Steady State ROI</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16A34A', textAlign: 'center' }}>
                {steadyStateROI.toFixed(1)}x
              </div>
              <div style={{ fontSize: '11px', color: '#14532D', textAlign: 'center', marginTop: '5px' }}>
                Full annual benefits realized
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Metrics Improvement */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          Metrics Improvement Summary
        </h2>

        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
          The metrics below show improvements for each individual hire. Your annual impact = per-hire savings × {formatNumber(getNumericValue(formData.annualHires))} hires/year.
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <thead>
            <tr style={{ backgroundColor: '#F3F4F6' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Key Metrics</th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                <div>Current</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#6B7280' }}>(per hire)</div>
              </th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                <div>With Rooster</div>
                <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#6B7280' }}>(per hire)</div>
              </th>
              <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {improvementData.map((item, index) => {
              // Calculate the improvement percentage to match the ROI Analysis screen
              let changeText;
              let changeSubtext = "per hire";
              
              if (item.name === "No-Show Rate") {
                // For No-Show Rate, show the reduction percentage
                // This matches the ROI screen calculation
                const reductionFactor = Math.min(0.60 * (getNumericValue(formData.tasImprovement, 20) / 30), 0.75);
                changeText = `-${Math.round(reductionFactor * 100)}%`;
                changeSubtext = "reduction";
              } else if (item.higherIsBetter) {
                // For metrics where higher is better (e.g., TAS Score)
                const improvementValue = ((item.after - item.before) / item.before) * 100;
                changeText = `+${Math.round(improvementValue)}%`;
              } else {
                // For metrics where lower is better (Time to Fill, Cost per Hire, Vacancy Cost)
                const improvementValue = ((item.before - item.after) / item.before) * 100;
                changeText = `-${Math.round(improvementValue)}%`;
              }

              // Format values with proper spacing and units
              const beforeText = item.unit && item.unit === '$' ?
                formatCurrency(item.before) :
                `${formatNumber(item.before)}${item.unit ? ' ' + item.unit : ''}`;

              const afterText = item.unit && item.unit === '$' ?
                formatCurrency(item.after) :
                `${formatNumber(item.after)}${item.unit ? ' ' + item.unit : ''}`;

              // Update label for No-Show Rate to Interview No-Show Rate
              const displayName = item.name === "No-Show Rate" ? "Interview No-Show Rate" : item.name;

              return (
                <tr key={index} style={{ backgroundColor: index % 2 === 1 ? '#F9FAFB' : 'white' }}>
                  <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>{displayName}</td>
                  <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>{beforeText}</td>
                  <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>{afterText}</td>
                  <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>
                    <div style={{ color: '#10B981', fontWeight: 'bold' }}>{changeText}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>{changeSubtext}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Explanatory notes for key metrics - aligned with ROI Analysis tooltips */}
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '20px' }}>
          <p style={{ marginBottom: '6px' }}><strong>Time to Fill:</strong> The average number of days from job posting to accepted offer. Automated scheduling reduces coordination delays and manual back-and-forth. Typical improvements range from 25-35% reduction in time to fill. This is the most impactful metric for ROI, as every day saved reduces vacancy costs and accelerates productivity gains.</p>
          <p style={{ marginBottom: '6px' }}><strong>Cost per Hire:</strong> The total cost associated with filling a position, including recruitment, administration, and interview expenses. Automation reduces administrative costs through elimination of manual scheduling tasks. Faster hiring processes decrease overhead expenses and recruiter time per position. Cost per hire affects recruiting efficiency and overall talent acquisition budget.</p>
          <p style={{ marginBottom: '6px' }}><strong>Vacancy Cost:</strong> The cost to your organization of having a position unfilled, calculated as daily vacancy cost × time to fill. Reducing the time-to-fill directly reduces the total vacancy cost per position. Rooster accelerates interview scheduling, which is typically the biggest source of delay in the hiring process.</p>
          <p style={{ marginBottom: '6px' }}><strong>Interview No-Show Rate:</strong> Percentage of candidates who miss interviews. Significant reduction achieved through automated reminders and improved scheduling flexibility. Based on 4 interviews per hire and $84/hour fully-loaded interviewer cost.</p>
        </div>
      </div>
      
      {/* Savings Breakdown */}
      <div className="savings-breakdown" style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          Savings Breakdown
        </h2>

        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
          The following table shows how implementing Rooster Scheduling will generate savings across multiple areas of your talent acquisition process:
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <thead>
            <tr style={{ backgroundColor: '#F3F4F6' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Category</th>
              <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Annual Savings</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>Vacancy Cost Reduction</td>
              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: '#10B981', fontWeight: 'bold' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.timeToFillSavings || results.savings?.timeToFillSavings || 0)}
              </td>
            </tr>
            <tr style={{ backgroundColor: '#F9FAFB' }}>
              <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>Hiring Cost Reduction</td>
              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: '#10B981', fontWeight: 'bold' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.costPerHireSavings || results.savings?.costPerHireSavings || 0)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>Productivity Gains</td>
              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: '#10B981', fontWeight: 'bold' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.productivityGain || results.savings?.productivityGain || 0)}
              </td>
            </tr>
            <tr style={{ backgroundColor: '#F9FAFB' }}>
              <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>Admin Time Savings</td>
              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: '#10B981', fontWeight: 'bold' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.schedulingTimeSavings || results.savings?.schedulingTimeSavings || 0)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px', borderBottom: '1px solid #E5E7EB' }}>No-Show Reduction Savings</td>
              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: '#10B981', fontWeight: 'bold' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.noShowReductionSavings || results.savings?.noShowReductionSavings || 0)}
              </td>
            </tr>
            <tr style={{ backgroundColor: '#F3F4F6', fontWeight: 'bold' }}>
              <td style={{ padding: '10px' }}>Total Annual Savings</td>
              <td style={{ padding: '10px', textAlign: 'right', color: '#10B981', fontSize: '18px' }}>
                {formatCurrency(enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Explanatory notes for each savings category - matched to Screen 4 tooltips */}
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '20px' }}>
          <p style={{ marginBottom: '6px' }}><strong>Vacancy Cost Reduction:</strong> Direct financial impact from reducing the Time to Fill. Each day you reduce in your hiring process saves {formatCurrency(getNumericValue(formData.dailyVacancyCost))} per position. Rooster reduces your Time to Fill by {formatNumber(Math.round(results.metrics.current.timeToFill) - Math.round(results.metrics.improved.timeToFill))} days on average.</p>

          <p style={{ marginBottom: '6px' }}><strong>Hiring Cost Reduction:</strong> Direct reduction of {formatCurrency(getNumericValue(formData.costPerHire) - Math.round(results.metrics.improved.costPerHire))} per hire by eliminating redundant scheduling tools (saving {formatCurrency(Math.round((getNumericValue(formData.costPerHire) - Math.round(results.metrics.improved.costPerHire)) * 0.3))} per hire), improving recruiter efficiency by 15% (saving {formatCurrency(Math.round((getNumericValue(formData.costPerHire) - Math.round(results.metrics.improved.costPerHire)) * 0.4))} per hire), and reducing time-to-fill through faster interview coordination (saving {formatCurrency(Math.round((getNumericValue(formData.costPerHire) - Math.round(results.metrics.improved.costPerHire)) * 0.3))} per hire).</p>

          <p style={{ marginBottom: '6px' }}><strong>Productivity Gains:</strong> Financial value of new hires starting {formatNumber(Math.round(results.metrics.current.timeToFill) - Math.round(results.metrics.improved.timeToFill))} days sooner. With a daily contribution value of {formatCurrency(getNumericValue(formData.avgSalary)/250 * 0.1)}, each accelerated hire generates {formatCurrency(getNumericValue(formData.avgSalary)/250 * 0.1 * (Math.round(results.metrics.current.timeToFill) - Math.round(results.metrics.improved.timeToFill)))} of additional value per hire.</p>

          <p style={{ marginBottom: '6px' }}><strong>Admin Time Savings:</strong> Quantifiable time saved by reducing administrative scheduling effort from {getNumericValue(formData.adminTimePerHire)} minutes to approximately {Math.round(getNumericValue(formData.adminTimePerHire) * (1 - ((results.savings?.schedulingTimeSavings || 0) / (getNumericValue(formData.annualHires) * 3 * 31.25))))} minutes per hire. This represents up to a {Math.round(((results.savings?.schedulingTimeSavings || 0) / (getNumericValue(formData.annualHires) * 3 * 31.25)) * 100)}% reduction in scheduling workload.</p>

          <p style={{ marginBottom: '6px' }}><strong>No-Show Reduction Savings:</strong> Value recovered by reducing interview no-show rates. Based on your {scenarioMode || 'moderate'} scenario assumptions, interview no-shows will be reduced significantly through automated reminders and better scheduling options, saving valuable interviewer time and reducing rescheduling efforts.</p>
        </div>
      </div>
      
      {/* Top ROI Drivers */}
      <div className="top-roi-drivers" style={{ marginBottom: '5px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          Your Top ROI Drivers
        </h2>
        
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '15px' }}>
          Based on your TAS component scores
        </div>

        <p style={{ marginBottom: '20px', fontSize: '14px' }}>
          These features are specifically recommended for your organization based on your current TAS scores. 
          Each addresses your weakest components and contributes directly to your projected {formatCurrency(results.enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0)} in annual savings.
        </p>

        {/* Dynamic ROI Drivers Grid */}
        <div className="roi-drivers-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
          {(() => {
            // Get recommended features based on weak components
            const recommendedFeatures = getRecommendedFeatures(results.tasComponents, 8);
            
            // Calculate ROI contribution for each feature
            const featuresWithROI = recommendedFeatures.map(feature => {
              const totalImpact = calculateFeatureTotalImpact(feature);
              // Estimate ROI contribution as percentage of total savings
              const roiContribution = (totalImpact / 100) * 0.15; // Each point contributes roughly 15% of savings
              const estimatedSavings = (results.savings?.totalAnnualSavings || 0) * roiContribution;
              
              return {
                ...feature,
                estimatedSavings,
                roiPercentage: Math.round(roiContribution * 100)
              };
            });
            
            return featuresWithROI.slice(0, 8).map((feature, index) => (
              <div 
                key={index}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  padding: '12px',
                  backgroundColor: '#FAFAFA',
                  borderLeft: '4px solid #0284c7',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div style={{ fontSize: '20px' }}>{feature.icon || '🚀'}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0284c7' }}>
                      {formatCurrency(feature.estimatedSavings)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#6B7280' }}>annual savings</div>
                  </div>
                </div>
                
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#1F2937', marginBottom: '5px' }}>
                  {feature.name}
                </div>
                
                <p style={{ fontSize: '12px', margin: '0 0 8px 0', color: '#4B5563', lineHeight: '1.4' }}>
                  {feature.whyItMatters}
                </p>
                
                <div style={{ 
                  borderTop: '1px solid #E5E7EB', 
                  paddingTop: '8px', 
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '11px', color: '#6B7280' }}>
                    {feature.roiPercentage}% of total ROI
                  </span>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Footer is added directly in the HTML output for better print control */}
    </div>
  );
};

// Main function to generate and save the PDF
export const generatePDFReport = async (
  formData: CalculatorFormData,
  results: CalculatorResults,
  getComponentData: () => any[],
  getImprovementData: () => any[],
  enhancedData?: {
    scenarioMode?: 'conservative' | 'moderate' | 'optimistic';
    annualSubscription?: number;
    implementationInvestment?: number;
    paybackPeriod?: number;
    year1ROI?: number;
    steadyStateROI?: number;
    enhancedSavings?: any;
    topPerformerTAS?: number;
    industryAverageTAS?: number;
    componentData?: any[];
    improvementData?: any[];
    tasComponents?: any;
    currentTAS?: number;
    projectedTAS?: number;
    roiDrivers?: any[];
  },
  sessionId: string = '',
  reportId?: string,
  isInline: boolean = false  // New parameter to display inline instead of popup
) => {
  
  // Create loading indicator only for popup mode
  let loadingDiv: HTMLDivElement | null = null;
  if (!isInline) {
    loadingDiv = document.createElement('div');
    loadingDiv.style.position = 'fixed';
    loadingDiv.style.top = '0';
    loadingDiv.style.left = '0';
    loadingDiv.style.width = '100%';
    loadingDiv.style.height = '100%';
    loadingDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
    loadingDiv.style.display = 'flex';
    loadingDiv.style.justifyContent = 'center';
    loadingDiv.style.alignItems = 'center';
    loadingDiv.style.zIndex = '9999';
    loadingDiv.style.fontSize = '18px';
    loadingDiv.style.color = '#0284c7';
    loadingDiv.style.fontWeight = 'bold';
    loadingDiv.innerText = 'Generating report...';
    document.body.appendChild(loadingDiv);
  }
  
  try {
    // Since we can't use Puppeteer in the browser, we'll go with the fallback approach
    // which is to generate an HTML document and open it in a new tab for printing/saving
    
    // Use fresh data from enhancedData if available, otherwise fall back to function calls
    const componentData = enhancedData?.componentData || getComponentData();

    // Get improvement data which now includes Vacancy Cost
    const baseImprovementData = enhancedData?.improvementData || getImprovementData();

    // Filter out TAS Score for the PDF report
    const improvementData = baseImprovementData.filter(item => item.name !== "TAS Score");

    // Use fresh TAS values if available
    const currentTAS = enhancedData?.currentTAS ?? results.currentTAS;
    const projectedTAS = enhancedData?.projectedTAS ?? results.projectedTAS;
    
    // Generate the HTML content without the transform section (we'll add it directly in the HTML template)
    const htmlContent = renderToString(
      <div className="pdf-container pdf-content" style={{ fontFamily: 'Arial, sans-serif', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <ROIAnalysisReport
          formData={formData}
          results={{...results, currentTAS, projectedTAS}}
          componentData={componentData}
          improvementData={improvementData}
          includeTransformSection={false} // Don't include transform section in the rendered HTML
          scenarioMode={enhancedData?.scenarioMode}
          annualSubscription={enhancedData?.annualSubscription}
          implementationInvestment={enhancedData?.implementationInvestment}
          paybackPeriod={enhancedData?.paybackPeriod}
          year1ROI={enhancedData?.year1ROI}
          steadyStateROI={enhancedData?.steadyStateROI}
          enhancedSavings={enhancedData?.enhancedSavings}
          topPerformerTAS={enhancedData?.topPerformerTAS}
          industryAverageTAS={enhancedData?.industryAverageTAS}
        />
      </div>
    );
    
    // Determine target window based on isInline flag
    let targetDocument: Document;
    
    if (isInline) {
      // Display in current window (for ReportViewer)
      targetDocument = document;
      // Clear the current document content
      document.body.innerHTML = '';
      document.head.innerHTML = '';
    } else {
      // Open in new popup window (for normal report generation)
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        // Remove loading indicator before showing alert
        if (loadingDiv && loadingDiv.parentNode) {
          loadingDiv.parentNode.removeChild(loadingDiv);
        }
        alert('Please allow popups for this website to generate the PDF report.\n\nTo enable popups:\n1. Look for the popup blocker icon in your browser\'s address bar\n2. Click it and select "Always allow popups from this site"\n3. Then try downloading the report again.');
        return;
      }
      
      targetDocument = printWindow.document;
      // Store reference to the window for PDF capture
      (window as any).reportWindow = printWindow;
    }

    // Get the current date for the filename
    const today = new Date();
    const dateString = today.toISOString().slice(0, 10);

    // Write the HTML directly to the target window with real Rooster logo
    targetDocument.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TAS and Rooster ROI Analysis</title>
        <meta charset="utf-8">
        <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' 'unsafe-eval' data: blob: https:; connect-src 'self' https://us-central1-rooster-tas.cloudfunctions.net https://*.googleapis.com https://firestore.googleapis.com; img-src 'self' data: blob: https:;">
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; color-adjust: exact; }
            @page { size: A4; margin: 10mm; }

            /* Allow content to flow naturally */
            #content-with-footer {
              page-break-inside: auto;
              break-inside: auto;
              display: block !important; /* Ensure block display for proper layout */
              margin-top: 20px; /* Reduce space between features and transform section */
            }

            /* Keep the transform section and footer together */
            .transform-section {
              page-break-before: always !important; /* Force new page before transform section */
              page-break-after: avoid !important;
              break-after: avoid !important;
            }

            /* Consistent padding for content */
            #content-with-footer {
              padding-top: 15px;
              margin-top: 10px;
            }

            /* Component descriptions can break if long */
            div[style*="borderLeft: 4px solid"] {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Ensure the footer stays with the transform section */
            .report-footer {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }

            /* Page break rules - keep headings with their content */
            /* Keep headings with their content */
            h2, h3 { 
              page-break-after: avoid;
              page-break-before: auto;
              orphans: 3;
            }

            /* Make sure all h2 headings stay with their content */
            /* Headings naturally don't break */
            h2, h3 { page-break-inside: avoid; }

            /* Force specific page breaks after certain sections */
            .roi-summary {
              page-break-before: always; /* Force new page before ROI Analysis Summary */
              page-break-after: auto;  /* Allow natural flow */
            }

            .savings-breakdown {
              page-break-before: auto; /* Allow natural flow instead of forcing new page */
              page-break-inside: auto; /* Allow breaking if needed for long content */
            }

            /* Key features section print rules */
            .key-features {
              page-break-before: auto; /* Allow natural flow instead of forcing new page */
              page-break-inside: auto; /* Allow breaking if needed */
            }

            /* Top ROI Drivers section print rules */
            .top-roi-drivers {
              page-break-before: always; /* Force new page before Top ROI Drivers */
              page-break-inside: auto; /* Allow breaking if needed for long content */
            }

            /* Normal spacing for content */
            #content-with-footer:first-child {
              padding-top: 15px;
            }

            /* Better text flow settings */
            body {
              orphans: 3;
              widows: 3;
            }

            /* The logo and header should stay at the top of every page */
            .header-wrapper {
              display: table;
              width: 100%;
            }

            /* Only critical elements should avoid breaking */
            table { page-break-inside: avoid; } /* Keep tables together */
            /* Allow lists and divs to break naturally for better flow */
            ul, ol { 
              page-break-inside: auto;
              orphans: 2;
              widows: 2;
            }

            /* Allow feature grid to break if needed for better page usage */
            .feature-grid { 
              page-break-inside: auto;
            }
            /* But keep individual feature cards together */
            .feature-grid > div { 
              page-break-inside: avoid;
            }

            /* Ensure no breaks in the TAS score comparison section */
            .tas-circle-container { page-break-inside: avoid; }

            /* Allow component descriptions to flow naturally */
            .component-description {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Ensure header elements repeat properly on each page */
            .header-container, .logo-header {
              display: table-header-group;
            }

            /* Make sure measurements print with exact colors */
            * {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            /* Hide the instruction panel when printing */
            .instruction-panel {
              display: none !important;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          
          .content-wrapper {
            max-width: 800px;
            margin: 0 auto;
            background-color: white;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            padding: 0;
            overflow: hidden; /* Prevents content from extending beyond container */
          }
          
          img { max-width: 100%; }
          svg { overflow: visible; max-width: 100%; }

          .logo-image {
            height: 60px;
            margin-bottom: 5px;
          }

          /* Header styles */
          .header-container {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #0284c7;
            color: white;
            padding: 15px;
            margin-bottom: 20px;
            width: auto;
            box-sizing: border-box;
          }

          .logo-container {
            display: flex;
            align-items: center;
          }
          
          /* Instructions panel */
          .instruction-panel {
            background-color: #F0F4FF;
            border: 1px solid #C7D2FE;
            border-radius: 8px;
            padding: 20px;
            margin: 0 auto 20px auto;
            max-width: 800px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          
          .instruction-panel h2 {
            color: #4338CA;
            font-size: 18px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .instruction-panel p {
            color: #4B5563;
            font-size: 14px;
            line-height: 1.5;
            margin-bottom: 15px;
          }
          
          .button-container {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 15px;
          }
          
          .action-button {
            background-color: #0284c7;
            color: white;
            border: none;
            border-radius: 4px;
            padding: 10px 20px;
            font-weight: bold;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            box-shadow: 0 2px 4px rgba(2, 132, 199, 0.2);
          }
          
          .action-button:hover {
            background-color: #4338CA;
          }
          
          .action-button svg {
            margin-right: 8px;
            width: 16px;
            height: 16px;
          }
          
          .print-button {
            background-color: #ffffff;
            color: #0284c7;
            border: 1px solid #0284c7;
          }
          
          .print-button:hover {
            background-color: #F5F3FF;
          }

          @media print {
            .header-container {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              display: flex !important;
              background-color: #0284c7 !important;
              color: white !important;
            }
            /* Hide status overlay when printing */
            #status-overlay {
              display: none !important;
            }
          }
          
          /* Status overlay removed - report shows immediately */
          
          /* Content shows immediately - no loading state needed */
        </style>
      </head>
      <body>
        <!-- Status overlay removed - report shows immediately -->
        
        <!-- Instruction panel (will not be printed) - Green "Your Report is Ready" style -->
        <div class="instruction-panel" style="
          background: linear-gradient(to right, #f0fdf4, #ecfdf5);
          border: 1px solid #bbf7d0;
          color: #1f2937;
          padding: 24px;
          text-align: center;
          margin: 20px auto;
          border-radius: 8px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          max-width: 800px;
        ">
          <!-- Success checkmark -->
          <div style="
            display: flex;
            justify-content: center;
            margin-bottom: 12px;
          ">
            <div style="
              background-color: #dcfce7;
              border-radius: 50%;
              padding: 12px;
            ">
              <svg style="width: 32px; height: 32px; color: #16a34a;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          <h2 style="margin: 0 0 16px 0; font-size: 24px; font-weight: bold; color: #1f2937;">Your Report is Ready</h2>
          
          ${reportId ? `
          <!-- Report Link Display - Enhanced -->
          <div style="margin-bottom: 24px; padding: 20px; background: linear-gradient(to bottom right, #eff6ff, #f0f9ff); border: 1px solid #bae6fd; border-radius: 8px; max-width: 36rem; margin-left: auto; margin-right: auto; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);">
            <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 12px;">
              <!-- Bookmark Icon -->
              <svg style="width: 20px; height: 20px; color: #0284c7; margin-top: 2px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
              </svg>
              <div style="flex: 1;">
                <p style="font-size: 16px; font-weight: 500; color: #374151; margin: 0;">Your permanent report link</p>
                <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">Bookmark or save it - this link never expires</p>
              </div>
            </div>
            <div style="display: flex; align-items: center; justify-content: space-between; background: white; padding: 12px; border-radius: 4px; border: 1px solid #e5e7eb;">
              <code style="font-size: 14px; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; font-family: monospace;">
                ${window.location.origin}/report/${reportId}
              </code>
              <button 
                type="button"
                id="copy-link-btn"
                style="margin-left: 8px; padding: 8px 16px; background: #0284c7; color: white; font-size: 14px; font-weight: 500; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center;"
                onmouseover="if(!this.dataset.copied) this.style.background='#0369a1'; else this.style.background='#059669';"
                onmouseout="if(!this.dataset.copied) this.style.background='#0284c7'; else this.style.background='#10b981';"
                onclick="
                  const url = '${window.location.origin}/report/${reportId}';
                  navigator.clipboard.writeText(url).then(() => {
                    const btn = document.getElementById('copy-link-btn');
                    if (btn) {
                      const originalHTML = btn.innerHTML;
                      btn.dataset.copied = 'true';
                      btn.innerHTML = '<svg style=\\'width: 16px; height: 16px; margin-right: 6px;\\' fill=\\'none\\' stroke=\\'currentColor\\' viewBox=\\'0 0 24 24\\'><path stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'2\\' d=\\'M5 13l4 4L19 7\\'></path></svg>Copied!';
                      btn.style.background = '#10b981';
                      setTimeout(() => {
                        btn.innerHTML = originalHTML;
                        btn.style.background = '#0284c7';
                        delete btn.dataset.copied;
                      }, 2000);
                    }
                  }).catch(() => {
                    alert('Failed to copy. Please select and copy the URL manually.');
                  });
                "
              >
                <svg style="width: 16px; height: 16px; margin-right: 6px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                Copy Link
              </button>
            </div>
          </div>
          ` : ''}
          
          <!-- Action buttons - Two row layout for better hierarchy -->
          <div style="margin-bottom: 24px;">
            <!-- Primary Actions Row -->
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 12px;">
              <button class="action-button" id="saveAsPdfButton" style="
                background: linear-gradient(to right, #0284c7, #06b6d4);
                color: white;
                padding: 12px 28px;
                border: none;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                transition: all 0.2s;
                min-width: 180px;
              " onmouseover="this.style.background='linear-gradient(to right, #0369a1, #0891b2)'" onmouseout="this.style.background='linear-gradient(to right, #0284c7, #06b6d4)'">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 20px; height: 20px; margin-right: 8px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                Download Report
              </button>
              
              ${reportId ? `
              <button class="action-button" id="emailReportButton" style="
                background: linear-gradient(to right, #dc2626, #ef4444);
                color: white;
                padding: 12px 28px;
                border: none;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                transition: all 0.2s;
                min-width: 180px;
              " onmouseover="this.style.background='linear-gradient(to right, #b91c1c, #dc2626)'" onmouseout="this.style.background='linear-gradient(to right, #dc2626, #ef4444)'">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 20px; height: 20px; margin-right: 8px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Report
              </button>
              ` : ''}
            </div>
            
            <!-- Secondary Actions Row -->
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
              ${reportId ? `
              <button class="action-button" id="editReportButton" style="
                background: #f3f4f6;
                color: #4b5563;
                padding: 10px 24px;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
                min-width: 160px;
              " onmouseover="this.style.background='#e5e7eb'; this.style.borderColor='#9ca3af';" onmouseout="this.style.background='#f3f4f6'; this.style.borderColor='#d1d5db';">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; margin-right: 6px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Report
              </button>
              ` : ''}
              
              <button class="action-button" id="closeTabButton" style="
                background: #f3f4f6;
                color: #4b5563;
                padding: 10px 24px;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
                min-width: 160px;
              " onmouseover="this.style.background='#e5e7eb'; this.style.borderColor='#9ca3af';" onmouseout="this.style.background='#f3f4f6'; this.style.borderColor='#d1d5db';">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; margin-right: 6px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                ${isInline ? 'Return' : 'Close'}
              </button>
            </div>
          </div>
        </div>

        <div class="content-wrapper">
          <!-- Header wrapper for proper paging -->
          <div class="header-wrapper">
            <!-- Logo header row -->
            <div class="logo-header" style="display: flex; justify-content: center; padding: 15px; border-bottom: 1px solid #E5E7EB;">
              <img
                src="/images/rooster-logo.png"
                alt="Rooster"
                class="logo-image"
                style="max-height: 60px; width: auto;"
                onerror="this.style.display='none'"
              />
            </div>

            <!-- Blue header for the title -->
            <div class="header-container">
              <h1 style="margin: 0; font-size: 24px; width: 100%; text-align: center;">TAS and Rooster ROI Analysis</h1>
            </div>
          </div>

          <div style="padding: 0 20px 20px 20px;">
            <div class="report-content">
              <!-- Main content without the transform section -->
              ${htmlContent.replace(/<div className="transform-section"[\s\S]*$/, '')}

              <!-- Transform section with footer as a single unit -->
              <div id="content-with-footer" style="break-inside: auto; page-break-before: auto; position: relative; margin-top: 0; padding-top: 5px;">
                <!-- Recreate transform section manually -->
                <div class="transform-section" style="
                  margin-top: 10px;
                  margin-bottom: 0;
                  padding: 25px 0;
                  background-color: rgba(2, 132, 199, 0.1);
                  border-radius: 8px;
                  text-align: center;
                ">
                  <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #0284c7;">Ready to Transform Your Interview Scheduling?</h3>

                  <div style="display: flex; justify-content: center; align-items: center; flex-wrap: wrap; max-width: 800px; margin: 0 auto;">
                    <div style="flex: 1 1 60%; padding: 0 15px; text-align: left; min-width: 300px;">
                      <p style="font-size: 15px; line-height: 1.4; margin-bottom: 10px; color: #374151;">
                        Based on your metrics, Rooster Scheduling can save your organization
                        <span style="color: #059669; font-weight: bold;"> ${formatCurrency(enhancedData?.enhancedSavings?.steadyStateSavings?.totalAnnualSavings || results.savings?.totalAnnualSavings || 0)} annually</span> while improving:
                      </p>
                      <ul style="font-size: 14px; line-height: 1.4; text-align: left; padding-left: 20px; margin: 0 0 10px 0; color: #374151;">
                        <li>Scheduling efficiency by up to 70%</li>
                        <li>Time to fill by ${Math.round(results.metrics.current.timeToFill) - Math.round(results.metrics.improved.timeToFill)} days on average</li>
                        <li>Candidate experience and interview show rates</li>
                        <li>Recruiter and hiring manager productivity</li>
                      </ul>
                    </div>

                    <div style="flex: 1 1 40%; padding: 0 15px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 200px;">
                      <a href="https://rooster.us/demo" target="_blank" rel="noopener noreferrer" style="text-decoration: none;">
                        <div style="
                          padding: 10px 20px;
                          background-color: #0284c7;
                          color: white;
                          border-radius: 4px;
                          font-weight: bold;
                          font-size: 15px;
                          margin-bottom: 15px;
                          margin-top: 5px;
                          cursor: pointer;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        ">
                          Schedule a Demo Today
                        </div>
                      </a>
                      <p style="font-size: 13px; margin: 0;">
                        www.rooster.us | sales@rooster.us
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Blue footer bar immediately after transform box -->
                <div class="report-footer" style="
                  background-color: #0284c7;
                  color: white;
                  padding: 8px 15px;
                  text-align: center;
                  font-size: 12px;
                  font-weight: normal;
                  margin-top: 20px;
                  border-radius: 0;
                ">
                  TAS and Rooster ROI Analysis | www.rooster.us | Generated on: ${new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Email Dialog Modal (hidden by default) -->
        <div id="emailDialog" style="
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 50;
          align-items: center;
          justify-content: center;
          padding: 16px;
        ">
          <div style="
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            max-width: 512px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
          ">
            <!-- Dialog Header -->
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 24px;
              border-bottom: 1px solid #e5e7eb;
            ">
              <div style="display: flex; align-items: center; gap: 8px;">
                <svg style="width: 24px; height: 24px; color: #dc2626;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h2 style="font-size: 20px; font-weight: 600; color: #111827; margin: 0;">Email Report</h2>
              </div>
              <button id="emailDialogClose" style="
                background: none;
                border: none;
                color: #9ca3af;
                cursor: pointer;
                padding: 0;
                transition: color 0.15s;
              " onmouseover="this.style.color='#6b7280'" onmouseout="this.style.color='#9ca3af'">
                <svg style="width: 24px; height: 24px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <!-- Dialog Body -->
            <form id="emailForm" style="padding: 24px;">
              <!-- Status Messages (initially hidden) -->
              <div id="emailSuccessMessage" style="
                display: none;
                align-items: center;
                gap: 8px;
                padding: 16px;
                background-color: #f0fdf4;
                border: 1px solid #86efac;
                border-radius: 8px;
                margin-bottom: 16px;
              ">
                <svg style="width: 20px; height: 20px; color: #16a34a; flex-shrink: 0;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p style="color: #166534; margin: 0;">Report sent successfully! Check your email.</p>
              </div>
              
              <div id="emailErrorMessage" style="
                display: none;
                align-items: center;
                gap: 8px;
                padding: 16px;
                background-color: #fef2f2;
                border: 1px solid #fecaca;
                border-radius: 8px;
                margin-bottom: 16px;
              ">
                <svg style="width: 20px; height: 20px; color: #dc2626; flex-shrink: 0;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p id="emailErrorText" style="color: #991b1b; margin: 0;">Failed to send email. Please try again.</p>
              </div>
              
              <!-- To Email -->
              <div style="margin-bottom: 16px;">
                <label for="recipientEmail" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  To <span style="color: #ef4444;">*</span>
                </label>
                <input type="email" id="recipientEmail" required style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="recipient@example.com" value="${formData.contactEmail || ''}"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- Recipient Name -->
              <div style="margin-bottom: 16px;">
                <label for="recipientName" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  Recipient Name
                </label>
                <input type="text" id="recipientName" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="John Doe" value="${formData.contactName || ''}"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- CC Email -->
              <div style="margin-bottom: 16px;">
                <label for="ccEmail" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  CC (Optional)
                </label>
                <input type="email" id="ccEmail" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="cc@example.com"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- Personal Message -->
              <div style="margin-bottom: 16px;">
                <label for="personalMessage" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  Personal Message (Optional)
                </label>
                <textarea id="personalMessage" rows="4" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  resize: vertical;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="Add a personal note to accompany the report..."
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'"></textarea>
                <p style="font-size: 12px; color: #6b7280; margin: 4px 0 0;">
                  This message will appear in the email body along with the report summary.
                </p>
              </div>
              
              <!-- Action Buttons -->
              <div style="display: flex; justify-content: flex-end; gap: 12px; padding-top: 8px;">
                <button type="button" id="emailCancelButton" style="
                  padding: 8px 16px;
                  color: #374151;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  background: white;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: all 0.15s;
                " onmouseover="this.style.backgroundColor='#f9fafb'" onmouseout="this.style.backgroundColor='white'">
                  Cancel
                </button>
                <button type="submit" id="emailSendButton" style="
                  padding: 8px 16px;
                  background: #dc2626;
                  color: white;
                  border: none;
                  border-radius: 6px;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  transition: all 0.15s;
                " onmouseover="this.style.background='#b91c1c'" onmouseout="this.style.background='#dc2626'">
                  <svg style="width: 16px; height: 16px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Report
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <script>
          // Function to trigger print dialog
          function printReport() {
            window.print();
          }
          
          // Function to save as PDF (also uses print dialog but with suggested settings)
          function saveAsPDF() {
            // Set title to include useful file name
            document.title = "TAS-ROI-Analysis-${dateString}";
            
            // Industry-standard cross-browser print solution
            setTimeout(function() {
              try {
                // Try execCommand first (works better in Safari despite being deprecated)
                document.execCommand('print', false, null);
              } catch(e) {
                // Fallback to window.print() for browsers that don't support execCommand
                window.print();
              }
            }, 250); // Small delay ensures content is fully loaded
          }
          
          // Add event listener to save button
          document.getElementById('saveAsPdfButton').addEventListener('click', saveAsPDF);
          
          // Add event listener to close button
          document.getElementById('closeTabButton').addEventListener('click', function() {
            ${isInline ? `
            // For inline display, check if accessed from admin panel
            const urlParams = new URLSearchParams(window.location.search);
            const fromAdmin = urlParams.get('from') === 'admin';
            if (fromAdmin) {
              // Use popup-style return for admin panel - focus parent tab and close
              try {
                if (window.opener && !window.opener.closed) {
                  window.opener.focus();
                }
                window.close();
              } catch (error) {
                // Fallback if tab closing fails
                window.location.href = '/management';
              }
            } else if (window.history.length > 1) {
              window.history.back();
            } else {
              // Fallback: detect current route and navigate appropriately
              const currentPath = window.location.pathname;
              if (currentPath.includes('/report/')) {
                // For ROI reports, try to determine original route from referrer or default to /
                window.location.href = document.referrer || '/';
              } else {
                window.location.href = '/';
              }
            }
            ` : `
            // For popup window, focus parent and close
            try {
              // Focus the parent window if it exists and is not closed
              if (window.opener && !window.opener.closed) {
                window.opener.focus();
              }
              
              // Close the popup window
              window.close();
              
              // Fallback check if window didn't close (some browsers restrict this)
              setTimeout(function() {
                if (!window.closed) {
                  alert('Please close this tab manually to return to the calculator.');
                }
              }, 100);
            } catch (error) {
              // Fallback for any errors
              window.close();
              if (!window.closed) {
                alert('Please close this tab manually to return to the calculator.');
              }
            }
            `}
          });
          
          ${reportId ? `
          // Add event listener to email button
          const emailButton = document.getElementById('emailReportButton');
          if (emailButton) {
            emailButton.addEventListener('click', function() {
              // Show the email dialog
              const dialog = document.getElementById('emailDialog');
              if (dialog) {
                dialog.style.display = 'flex';
              }
            });
          }
          
          // Add event listener to edit button
          const editButton = document.getElementById('editReportButton');
          if (editButton) {
            editButton.addEventListener('click', function() {
              // Navigate to calculator with edit parameter
              const editUrl = '/?editReport=${reportId}';
              ${isInline ? `
              // For inline display, navigate in current window
              window.location.href = editUrl;
              ` : `
              // For popup, open in parent window and close popup
              if (window.opener && !window.opener.closed) {
                window.opener.location.href = editUrl;
                window.close();
              } else {
                // If no parent window, navigate in current window
                window.location.href = editUrl;
              }
              `}
            });
          }
          ` : ''}
          
          // Report shows immediately - no PDF generation needed
          
          // Email Dialog Functionality
          ${reportId ? `
          // Store report data for email
          const reportData = {
            sessionId: '${sessionId || reportId}',  // Use reportId as fallback when viewing from direct link
            reportId: '${reportId}',
            reportUrl: '${window.location.origin}/report/${reportId}',
            formData: ${JSON.stringify(formData)},
            results: ${JSON.stringify(results)},
            enhancedData: ${JSON.stringify(enhancedData || {})}
          };
          
          // Close dialog handlers
          const emailDialogClose = document.getElementById('emailDialogClose');
          const emailCancelButton = document.getElementById('emailCancelButton');
          const emailDialog = document.getElementById('emailDialog');
          
          function closeEmailDialog() {
            if (emailDialog) {
              emailDialog.style.display = 'none';
              // Reset messages
              document.getElementById('emailSuccessMessage').style.display = 'none';
              document.getElementById('emailErrorMessage').style.display = 'none';
            }
          }
          
          if (emailDialogClose) {
            emailDialogClose.addEventListener('click', closeEmailDialog);
          }
          
          if (emailCancelButton) {
            emailCancelButton.addEventListener('click', closeEmailDialog);
          }
          
          // Handle form submission
          const emailForm = document.getElementById('emailForm');
          if (emailForm) {
            emailForm.addEventListener('submit', async function(e) {
              e.preventDefault();
              
              // Get form values
              const recipientEmail = document.getElementById('recipientEmail').value;
              const recipientName = document.getElementById('recipientName').value;
              const ccEmail = document.getElementById('ccEmail').value;
              const personalMessage = document.getElementById('personalMessage').value;
              
              // Hide previous messages
              document.getElementById('emailSuccessMessage').style.display = 'none';
              document.getElementById('emailErrorMessage').style.display = 'none';
              
              // Disable send button and show loading state
              const sendButton = document.getElementById('emailSendButton');
              const originalButtonHTML = sendButton.innerHTML;
              sendButton.disabled = true;
              sendButton.innerHTML = '<svg style="width: 16px; height: 16px; animation: spin 1s linear infinite;" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Sending...';
              
              try {
                // Call Firebase function
                const response = await fetch('https://us-central1-rooster-tas.cloudfunctions.net/sendROIReportEmail', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    data: {
                      recipientEmail: recipientEmail,
                      recipientName: recipientName || reportData.formData.contactName || 'Valued Customer',
                      ccEmail: ccEmail || undefined,
                      personalMessage: personalMessage || undefined,
                      senderName: reportData.formData.contactName || undefined,
                      sessionId: reportData.sessionId,
                      reportId: reportData.reportId,  // Pass reportId for better session resolution
                      reportUrl: reportData.reportUrl,
                      formData: reportData.formData,
                      results: reportData.results,
                      enhancedData: reportData.enhancedData
                    }
                  })
                });
                
                const result = await response.json();
                
                if (result.result && result.result.success) {
                  // Show success message
                  document.getElementById('emailSuccessMessage').style.display = 'flex';
                  
                  // Close dialog after 2 seconds
                  setTimeout(function() {
                    closeEmailDialog();
                    // Reset form
                    document.getElementById('recipientEmail').value = reportData.formData.contactEmail || '';
                    document.getElementById('recipientName').value = reportData.formData.contactName || '';
                    document.getElementById('ccEmail').value = '';
                    document.getElementById('personalMessage').value = '';
                  }, 2000);
                } else {
                  // Show error message
                  document.getElementById('emailErrorText').textContent = (result.error && result.error.message) || 'Failed to send email. Please try again.';
                  document.getElementById('emailErrorMessage').style.display = 'flex';
                }
              } catch (error) {
                // Show error message
                document.getElementById('emailErrorText').textContent = 'An error occurred. Please try again.';
                document.getElementById('emailErrorMessage').style.display = 'flex';
                console.error('Email send error:', error);
              } finally {
                // Re-enable send button
                sendButton.disabled = false;
                sendButton.innerHTML = originalButtonHTML;
              }
            });
          }
          
          // Add spin animation style
          const style = document.createElement('style');
          style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
          document.head.appendChild(style);
          ` : ''}
        </script>
      </body>
      </html>
    `);

      // Close the document write operation
      targetDocument.close();
    
  } catch (error) {
    console.error('Error generating report:', error);
    alert('There was an error generating the PDF. Please try again.');
  } finally {
    // Remove loading indicator if it exists
    if (loadingDiv && loadingDiv.parentNode) {
      loadingDiv.parentNode.removeChild(loadingDiv);
    }
  }
};

export default generatePDFReport;